import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { VariationSetsAPI } from "@/lib/api";

// Mirrors the table in NewVariationSet.jsx. Keep them in sync if changing.
const MODEL_TIMING = {
  "gemini-2.5-flash":           { mins_per_var: 0.5, extra: 0.2, label: "Gemini 2.5 Flash" },
  "gemini-2.5-pro":             { mins_per_var: 1.0, extra: 0.4, label: "Gemini 2.5 Pro" },
  "claude-haiku-4-5-20251001":  { mins_per_var: 1.5, extra: 0.5, label: "Claude Haiku 4.5" },
  "claude-sonnet-4-5-20250929": { mins_per_var: 3.0, extra: 1.0, label: "Claude Sonnet 4.5" },
};

function estimateRemainingMinutes(vset) {
  const m = MODEL_TIMING[vset.model_name];
  if (!m) return null;
  const nVars = (vset.variations || []).length || 1;
  const total = m.mins_per_var + Math.max(0, nVars - 1) * m.extra + 0.4;
  const p = vset.progress || {};
  const done = p.current || 0;
  const tot = p.total || 1;
  const fractionDone = Math.min(1, done / tot);
  // Linear remaining estimate. Bottom out at 0.3 min so the panel never reads "0".
  const remaining = Math.max(0.3, total * (1 - fractionDone));
  return Math.round(remaining * 2) / 2;
}

function formatMinutes(mins) {
  if (mins == null) return "—";
  if (mins < 1) return `${Math.round(mins * 60)}s`;
  if (mins === Math.floor(mins)) return `~${mins} min`;
  return `~${mins.toFixed(1)} min`;
}

export default function VariationSetView() {
  const { id } = useParams();
  const [vset, setVset] = useState(null);

  const load = useCallback(async () => {
    const { data } = await VariationSetsAPI.get(id);
    setVset(data);
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // Poll while processing
  useEffect(() => {
    if (!vset || vset.status === "complete" || vset.status === "failed") return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [vset, load]);

  if (!vset) {
    return (
      <div className="v-container" style={{ paddingTop: 80 }}>
        <div className="overline">Loading…</div>
      </div>
    );
  }

  const variations = vset.variations || [];
  const comparison = vset.comparison || {};
  const byId = Object.fromEntries(variations.map((v) => [v.variation_id, v]));
  const rankingIds = comparison.ranking || variations.map((v) => v.variation_id);
  const recommendedId =
    comparison.recommended_variation_id || (rankingIds[0] || variations[0]?.variation_id);
  const winnerByDim = Object.fromEntries(
    (comparison.dimension_winners || []).map((w) => [w.key, w]),
  );
  const dimKeys = variations[0]?.dimensions?.map((d) => d.key) || [];

  return (
    <div className="v-container" style={{ paddingTop: 48, paddingBottom: 96 }}>
      <Link to={`/projects/${vset.project_id}`} className="overline" data-testid="back-to-project">
        ← Project
      </Link>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 24, marginTop: 16 }}>
        <div>
          <div className="overline">
            Variation comparison · {variations.length} variations · {vset.status}
          </div>
          <h1 className="display" style={{ fontSize: "clamp(40px,5vw,72px)", lineHeight: 1, marginTop: 10 }}>
            {vset.title}
          </h1>
          <div className="mono" style={{ color: "var(--v-text-2)", marginTop: 6 }}>
            by {vset.designer_name} · {new Date(vset.created_at).toLocaleString()}
          </div>
        </div>
        {vset.status === "complete" && (
          <a
            className="v-btn"
            href={VariationSetsAPI.exportUrl(id, "pdf")}
            data-testid="vset-pdf-download"
            target="_blank"
            rel="noreferrer"
          >
            Download PDF →
          </a>
        )}
      </div>

      {vset.status === "processing" && <ProgressPanel vset={vset} />}
      {vset.status === "failed" && (
        <div className="v-surface" style={{ padding: 24, marginTop: 32 }} data-testid="vset-failed">
          <div className="overline" style={{ color: "var(--v-accent)" }}>Failed</div>
          <p style={{ marginTop: 8, color: "var(--v-text-2)" }}>{vset.error_message || "Background analysis failed."}</p>
        </div>
      )}

      {vset.status === "complete" && (
        <>
          <RecommendedPanel
            vset={vset}
            recommended={byId[recommendedId]}
            rationale={comparison.rationale}
          />

          <ComparisonTable
            variations={variations}
            dimKeys={dimKeys}
            recommendedId={recommendedId}
            winnerByDim={winnerByDim}
          />

          <VariationGallery
            vset={vset}
            variations={variations.map((v) => byId[v.variation_id])}
            rankingIds={rankingIds}
          />
        </>
      )}
    </div>
  );
}

function ProgressPanel({ vset }) {
  const p = vset.progress || {};
  const total = p.total || 1;
  const current = p.current || 0;
  const pct = Math.min(100, Math.round((current / total) * 100));
  const variationCount = (vset.variations || []).length;
  const modelLabel = MODEL_TIMING[vset.model_name]?.label || vset.model_name;
  const remaining = estimateRemainingMinutes(vset);
  const phaseLabel =
    p.phase === "comparing"
      ? "Cross-comparing all variations side-by-side…"
      : `Auditing ${variationCount} variations · ${current}/${total} dimensions scored`;
  return (
    <div className="v-surface" style={{ padding: 28, marginTop: 32 }} data-testid="vset-progress">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
        <div>
          <div className="overline">In progress · {modelLabel}</div>
          <h2 style={{ fontSize: 20, marginTop: 6 }}>{phaseLabel}</h2>
        </div>
        <div style={{ textAlign: "right" }} data-testid="vset-time-remaining">
          <div className="overline" style={{ color: "var(--v-text-2)" }}>Time remaining</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: "var(--v-accent)", lineHeight: 1, marginTop: 4 }}>
            {formatMinutes(remaining)}
          </div>
        </div>
      </div>

      <div style={{ height: 4, background: "var(--v-border)", marginTop: 18 }}>
        <div style={{ width: `${pct}%`, height: 4, background: "var(--v-accent)", transition: "width .4s ease" }} />
      </div>

      <div style={{
        marginTop: 18,
        padding: "14px 16px",
        background: "color-mix(in srgb, var(--v-accent) 6%, transparent)",
        borderLeft: "3px solid var(--v-accent)",
      }}>
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.55, color: "var(--v-text)" }}>
          <b>You can leave this tab.</b> The audit keeps running on our servers. Come back in
          about <b>{formatMinutes(remaining)}</b> and the verdict will be waiting here. No need to refresh —
          this view auto-updates every few seconds.
        </p>
      </div>
    </div>
  );
}

function RecommendedPanel({ vset, recommended, rationale }) {
  if (!recommended) return null;
  return (
    <div className="v-surface" style={{ padding: 32, marginTop: 32, borderLeft: "3px solid var(--v-accent)" }} data-testid="vset-recommended">
      <div className="overline">Recommended pick</div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 24, marginTop: 8, flexWrap: "wrap" }}>
        <h2 style={{ fontSize: 40, lineHeight: 1, margin: 0 }}>{recommended.name}</h2>
        <div style={{ fontSize: 48, fontWeight: 800, color: "var(--v-accent)", lineHeight: 1 }}>
          {Number(recommended.overall_score || 0).toFixed(1)}
          <span className="mono" style={{ fontSize: 12, color: "var(--v-text-2)", marginLeft: 6, fontWeight: 400 }}>/10</span>
        </div>
      </div>
      {rationale && (
        <p style={{ marginTop: 18, fontSize: 16, lineHeight: 1.6, color: "var(--v-text)" }}>{rationale}</p>
      )}
    </div>
  );
}

function ComparisonTable({ variations, dimKeys, recommendedId, winnerByDim }) {
  return (
    <div className="v-surface" style={{ padding: 0, marginTop: 24, overflow: "hidden" }} data-testid="vset-table">
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 640 }}>
          <thead>
            <tr>
              <th style={th()}>Dimension</th>
              {variations.map((v) => (
                <th
                  key={v.variation_id}
                  style={{ ...th(), textAlign: "center", color: v.variation_id === recommendedId ? "var(--v-accent)" : "var(--v-text-2)" }}
                  data-testid={`vset-th-${v.variation_id}`}
                >
                  {v.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dimKeys.map((k) => {
              const label = variations.find((v) => v.dimensions.some((d) => d.key === k))?.dimensions.find((d) => d.key === k)?.label || k;
              const winner = winnerByDim[k];
              return (
                <React.Fragment key={k}>
                  <tr>
                    <th style={tdLabel()}>{label}</th>
                    {variations.map((v) => {
                      const dim = v.dimensions.find((d) => d.key === k);
                      const score = dim?.score;
                      const isWin = winner?.winner_variation_id === v.variation_id;
                      return (
                        <td key={v.variation_id} style={tdCell(isWin)} data-testid={`vset-cell-${k}-${v.variation_id}`}>
                          <div style={{ fontSize: 22, fontWeight: 700, color: isWin ? "var(--v-accent)" : "var(--v-text-2)", lineHeight: 1 }}>
                            {score == null ? "—" : Number(score).toFixed(1)}
                          </div>
                          {isWin && (
                            <div className="overline" style={{ color: "var(--v-accent)", marginTop: 4, fontSize: 9 }}>
                              Winner
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                  {winner?.reason && (
                    <tr>
                      <td colSpan={variations.length + 1} style={{ padding: "0 16px 12px", borderBottom: "1px solid var(--v-border)" }}>
                        <div style={{ fontSize: 13, color: "var(--v-text-2)", fontStyle: "italic", lineHeight: 1.5 }}>
                          {winner.reason}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            <tr style={{ background: "color-mix(in srgb, var(--v-bg) 50%, var(--v-surface))" }}>
              <th style={{ ...tdLabel(), textTransform: "uppercase", letterSpacing: ".12em", fontSize: 11 }}>Overall</th>
              {variations.map((v) => {
                const isRec = v.variation_id === recommendedId;
                return (
                  <td key={v.variation_id} style={{ ...tdCell(isRec) }} data-testid={`vset-overall-${v.variation_id}`}>
                    <div style={{ fontSize: 28, fontWeight: 800, color: isRec ? "var(--v-accent)" : "var(--v-text)", lineHeight: 1 }}>
                      {Number(v.overall_score || 0).toFixed(1)}
                    </div>
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function VariationGallery({ vset, variations, rankingIds }) {
  const ranked = rankingIds.map((vid) => variations.find((v) => v && v.variation_id === vid)).filter(Boolean);
  return (
    <div style={{ marginTop: 48 }}>
      <div className="overline" style={{ marginBottom: 16 }}>By rank · each variation's deep-dive</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {ranked.map((v, idx) => (
          <div key={v.variation_id} className="v-surface" style={{ padding: 20 }} data-testid={`vset-detail-${v.variation_id}`}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
              <div>
                <div className="overline">Rank #{idx + 1}</div>
                <h3 style={{ fontSize: 22, marginTop: 6 }}>{v.name}</h3>
              </div>
              <div style={{ fontSize: 28, fontWeight: 800, color: "var(--v-accent)", lineHeight: 1 }}>
                {Number(v.overall_score || 0).toFixed(1)}
              </div>
            </div>
            <img
              src={VariationSetsAPI.imageUrl(vset.variation_set_id, v.variation_id, 0)}
              alt={v.name}
              style={{ width: "100%", marginTop: 14, border: "1px solid var(--v-border)", aspectRatio: "1/1", objectFit: "cover" }}
            />
            {v.summary && (
              <p style={{ marginTop: 12, fontSize: 14, lineHeight: 1.55, color: "var(--v-text-2)" }}>{v.summary}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const th = () => ({
  padding: "16px 16px",
  textAlign: "left",
  fontFamily: "JetBrains Mono, monospace",
  fontSize: 11,
  letterSpacing: ".12em",
  textTransform: "uppercase",
  color: "var(--v-text-2)",
  borderBottom: "1px solid var(--v-border)",
});

const tdLabel = () => ({
  padding: "14px 16px",
  textAlign: "left",
  fontSize: 14,
  fontWeight: 600,
  color: "var(--v-text)",
  borderBottom: "1px solid var(--v-border)",
  verticalAlign: "middle",
});

const tdCell = (isWin) => ({
  padding: "14px 16px",
  textAlign: "center",
  borderBottom: "1px solid var(--v-border)",
  background: isWin ? "color-mix(in srgb, var(--v-accent) 8%, transparent)" : "transparent",
  verticalAlign: "middle",
});
